import React, { useEffect, useState } from 'react';
import { Loader2, Printer, RefreshCw, RotateCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const STATUS = {
  demo_not_sent: ['Demo (not sent)', 'bg-amber-100 text-amber-800'],
  Queued: ['Queued', 'bg-blue-100 text-blue-800'],
  Sending: ['Sending', 'bg-blue-100 text-blue-800'],
  Sent: ['Sent', 'bg-green-100 text-green-800'],
  SendingFailed: ['Failed', 'bg-red-100 text-red-800'],
  Resent: ['Resent', 'bg-gray-100 text-gray-700'],
};

const FaxRow = ({ f, onResend, onRefresh, busy }) => {
  const [label, cls] = STATUS[f.status] || [f.status, 'bg-gray-100 text-gray-700'];
  return (
    <div className="flex items-center gap-3 border rounded-md p-3" data-testid={`fax-row-${f.id}`}>
      <Printer className="w-4 h-4 text-gray-500 shrink-0" />
      <div className="flex-1 min-w-0 text-sm">
        <p className="font-medium">CMS-485 to {f.physicianName || 'physician'} at {f.to}</p>
        <p className="text-xs text-gray-500">
          {new Date(f.createdAt).toLocaleString()} | {f.pages} page(s) | attempt {f.attempt || 1}{f.autoRetry ? ' (automatic retry)' : ''}{f.provider === 'ringcentral' ? ' | RingCentral' : ' | demo'}
        </p>
      </div>
      <span className={`text-xs px-2 py-1 rounded-full font-medium shrink-0 ${cls}`} data-testid={`fax-status-${f.id}`}>{label}</span>
      {f.provider === 'ringcentral' && !['Sent', 'SendingFailed', 'Resent'].includes(f.status) && (
        <button onClick={() => onRefresh(f)} className="p-1.5 rounded hover:bg-gray-100 shrink-0" title="Check status" data-testid={`fax-refresh-${f.id}`}><RefreshCw className="w-4 h-4" /></button>
      )}
      {f.requestSigned && <span className="text-xs text-green-700 shrink-0" data-testid={`fax-signed-${f.id}`}>Physician signed</span>}
      {!f.resentAs && !f.requestSigned && (
        <button onClick={() => onResend(f)} disabled={busy === f.id} className="shrink-0 px-3 py-1.5 text-sm rounded border hover:bg-gray-50 flex items-center gap-1 disabled:opacity-50" data-testid={`fax-resend-${f.id}`}>
          {busy === f.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCw className="w-4 h-4" />} Resend
        </button>
      )}
    </div>
  );
};

export const PatientFaxLog = ({ patientId }) => {
  const [faxes, setFaxes] = useState(null);
  const [busy, setBusy] = useState('');
  const load = () => api.get('/fax-logs', { params: { patientId } }).then(r => setFaxes(r.data.faxes || [])).catch(() => setFaxes([]));
  useEffect(() => { load(); }, [patientId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resend = async (f) => {
    setBusy(f.id);
    try {
      const r = await api.post(`/fax-logs/${f.id}/resend`);
      toast.success(r.data.message);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not resend');
    } finally {
      setBusy('');
    }
  };
  const refresh = async (f) => {
    try {
      const r = await api.get(`/fax-logs/${f.id}/status`);
      setFaxes(list => list.map(x => (x.id === f.id ? r.data.fax : x)));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not check status');
    }
  };

  return (
    <div className="bg-white rounded border p-4" data-testid="patient-fax-log">
      <h3 className="font-semibold text-gray-800 flex items-center gap-2 mb-1"><Printer className="w-4 h-4" /> Faxes</h3>
      <p className="text-xs text-gray-500 mb-3">CMS-485 signature faxes for this patient. A failed fax is retried once automatically, then shows Failed.</p>
      {faxes === null ? <Loader2 className="w-5 h-5 animate-spin" />
        : faxes.length === 0 ? <p className="text-sm text-gray-500" data-testid="patient-fax-empty">No faxes sent for this patient yet.</p>
          : <div className="space-y-2">{faxes.map(f => <FaxRow key={f.id} f={f} onResend={resend} onRefresh={refresh} busy={busy} />)}</div>}
    </div>
  );
};
